import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
  computed,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { PhoneInputComponent } from '../../../../ui/phone-input/phone-input';
import { ServiceCardComponent } from '../service-card/service-card';
import { ProfessionalCardComponent } from '../professional-card/professional-card';
import { TimeSlotSelectorComponent } from '../time-slot-selector/time-slot-selector';
import {
  AvailabilityCalendarComponent,
  DayAvailability,
} from '../availability-calendar/availability-calendar';

export interface ServiceData {
  serviceId: string;
  name: string;
  description?: string | null;
  price: number;
  durationMinutes?: number | null;
  imageUrl?: string | null;
}

export interface ProfessionalData {
  userId: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  color?: string | null;
  avatarUrl?: string | null;
}

export interface TimeSlot {
  value: string;
  label: string;
}

export interface BookingWizardData {
  serviceId: string;
  professionalId?: string | null;
  date: string;
  time: string;
  clientName: string;
  phone: { countryId: string; phoneNumber: string };
}

@Component({
  selector: 'app-booking-wizard',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PhoneInputComponent,
    ServiceCardComponent,
    ProfessionalCardComponent,
    TimeSlotSelectorComponent,
    AvailabilityCalendarComponent,
  ],
  templateUrl: './booking-wizard.html',
  styleUrl: './booking-wizard.css',
})
export class BookingWizardComponent implements AfterViewInit, OnChanges {
  private fb = inject(FormBuilder);

  @Input() services: ServiceData[] = [];
  @Input() professionals: ProfessionalData[] = [];
  @Input() timeSlots: TimeSlot[] = [];
  @Input() businessSchedule: Array<{
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    isActive: boolean;
  }> = [];
  @Input() dayAvailabilities: DayAvailability[] = [];
  @Input() isLoadingMonthAvailability: boolean = false;
  @Input() primaryColor: string = '#2563eb';
  @Input() businessName: string = '';
  /** Service picked on the landing menu; preselects it and skips step 1 */
  @Input() initialServiceId: string | null = null;
  @Input() isLoadingServices: boolean = false;
  @Input() isLoadingProfessionals: boolean = false;
  @Input() isLoadingTimeSlots: boolean = false;
  @Input() isSubmitting: boolean = false;

  @Output() stepChange = new EventEmitter<number>();
  @Output() bookingComplete = new EventEmitter<BookingWizardData>();
  @Output() serviceSelected = new EventEmitter<string>();
  @Output() professionalSelected = new EventEmitter<string | null>();
  @Output() dateSelected = new EventEmitter<string>();
  @Output() monthChanged = new EventEmitter<{ year: number; month: number }>();
  @Output() cancelWizard = new EventEmitter<void>();

  currentStep = signal<number>(1);
  totalSteps = 4;

  steps = [
    { number: 1, label: 'Servicio' },
    { number: 2, label: 'Profesional' },
    { number: 3, label: 'Fecha y hora' },
    { number: 4, label: 'Tus datos' },
  ];

  form: FormGroup = this.fb.group({
    serviceId: ['', Validators.required],
    professionalId: [''],
    date: ['', Validators.required],
    time: ['', Validators.required],
    clientName: ['', [Validators.required, Validators.minLength(2)]],
    phone: [null, Validators.required],
  });

  // Track form values to trigger computed updates
  private serviceIdValue = signal<string>('');
  private professionalIdValue = signal<string>('');
  dateValue = signal<string>('');
  timeValue = signal<string>('');
  private clientNameValue = signal<string>('');
  private phoneValue = signal<any>(null);
  private timeSlotsValue = signal<TimeSlot[]>([]);

  selectedService = computed<ServiceData | null>(() => {
    const serviceId = this.serviceIdValue();
    if (!serviceId) return null;
    return this.services.find((s) => s.serviceId === serviceId) ?? null;
  });

  selectedProfessional = computed<ProfessionalData | null>(() => {
    const professionalId = this.professionalIdValue();
    if (!professionalId) return null;
    return this.professionals.find((p) => p.userId === professionalId) ?? null;
  });

  // Formatted selected date for display
  formattedSelectedDate = computed(() => {
    const dateStr = this.dateValue();
    if (!dateStr) return '';
    const parts = dateStr.split('-').map(Number);
    if (parts.length !== 3) return dateStr;
    const [year, month, day] = parts;
    const date = new Date(year, month - 1, day);
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    return `${days[date.getDay()]} ${day} ${months[month - 1]}`;
  });

  // Display label of the selected time slot (e.g. "3:00 p.m.")
  selectedTimeLabel = computed(() => {
    const time = this.timeValue();
    if (!time) return '';
    return this.timeSlotsValue().find((slot) => slot.value === time)?.label ?? time;
  });

  // One-line summary for the mobile dock: professional, day and time
  dockDetails = computed(() => {
    const prof = this.selectedProfessional();
    const parts = [
      prof ? `${prof.firstName} ${prof.lastName}`.trim() : '',
      this.formattedSelectedDate(),
      this.selectedTimeLabel(),
    ].filter(Boolean);
    return parts.join(', ');
  });

  canProceedToNextStep = computed(() => {
    const step = this.currentStep();
    switch (step) {
      case 1:
        return !!this.serviceIdValue() && this.form.get('serviceId')?.valid;
      case 2:
        return !!this.selectedProfessional();
      case 3:
        return !!this.dateValue() && !!this.timeValue() &&
               this.form.get('date')?.valid && this.form.get('time')?.valid;
      case 4:
        return !!this.clientNameValue() && !!this.phoneValue() &&
               this.form.get('clientName')?.valid && this.form.get('phone')?.valid;
      default:
        return false;
    }
  });

  ngAfterViewInit(): void {
    // Load cached client data
    this.loadCachedClientData();

    // Subscribe to form changes to update signals
    this.form.get('serviceId')?.valueChanges.subscribe(value => {
      this.serviceIdValue.set(value || '');
    });
    this.form.get('professionalId')?.valueChanges.subscribe(value => {
      this.professionalIdValue.set(value || '');
    });
    this.form.get('date')?.valueChanges.subscribe(value => {
      this.dateValue.set(value || '');
    });
    this.form.get('time')?.valueChanges.subscribe(value => {
      this.timeValue.set(value || '');
    });
    this.form.get('clientName')?.valueChanges.subscribe(value => {
      this.clientNameValue.set(value || '');
    });
    this.form.get('phone')?.valueChanges.subscribe(value => {
      this.phoneValue.set(value);
    });

    // Service chosen on the landing: select it and move on to the professional step
    if (this.initialServiceId) {
      const serviceId = this.initialServiceId;
      setTimeout(() => this.selectService(serviceId));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['timeSlots']) {
      this.timeSlotsValue.set(this.timeSlots || []);
    }
    if (changes['professionals']) {
      const professionalId = this.professionalIdValue();
      if (!professionalId) return;
      const isStillValid = this.professionals.some((p) => p.userId === professionalId);
      if (!isStillValid) {
        this.form.patchValue({ professionalId: '' }, { emitEvent: false });
        this.professionalIdValue.set('');
        this.professionalSelected.emit(null);
      }
    }
  }

  private cachedPhone: { countryId: string; phoneNumber: string } | null = null;

  private loadCachedClientData(): void {
    try {
      const cached = localStorage.getItem('citalink_booking_client_data');
      if (cached) {
        const data = JSON.parse(cached);
        if (data.clientName) {
          this.form.patchValue({ clientName: data.clientName }, { emitEvent: false });
          this.clientNameValue.set(data.clientName);
          const clientNameControl = this.form.get('clientName');
          if (clientNameControl) {
            clientNameControl.updateValueAndValidity();
          }
        }
        if (data.phone && data.phone.countryId && data.phone.phoneNumber) {
          // Store cached phone to apply when step 4 renders
          this.cachedPhone = data.phone;
          // Set form value - the PhoneInputComponent will receive it via writeValue when it renders
          this.form.patchValue({ phone: data.phone }, { emitEvent: false });
          this.phoneValue.set(data.phone);
        }
      }
    } catch (error) {
      console.warn('Error loading cached client data:', error);
    }
  }

  /** Apply cached phone data when step 4 renders (phone-input is now in the DOM) */
  private applyCachedPhone(): void {
    if (this.cachedPhone) {
      // Re-apply phone value now that the phone-input component is rendered
      setTimeout(() => {
        this.form.patchValue({ phone: this.cachedPhone });
        this.phoneValue.set(this.cachedPhone);
        this.cachedPhone = null;
      }, 200);
    }
  }

  private saveCachedClientData(): void {
    try {
      const formValue = this.form.getRawValue();
      // Store clean phone data - ensure phoneNumber is digits only
      let phone = formValue.phone || null;
      if (phone && phone.phoneNumber) {
        phone = {
          countryId: phone.countryId,
          phoneNumber: phone.phoneNumber.replace(/\D/g, ''),
        };
      }
      const data = {
        clientName: formValue.clientName || '',
        phone,
      };
      localStorage.setItem('citalink_booking_client_data', JSON.stringify(data));
    } catch (error) {
      console.warn('Error saving cached client data:', error);
    }
  }

  selectService(serviceId: string): void {
    if (this.serviceIdValue() !== serviceId) {
      // A different service can change who offers it and how long it lasts
      this.clearSelectedDate();
    }
    this.form.patchValue({ serviceId });
    this.form.get('serviceId')?.markAsTouched();
    this.form.get('serviceId')?.updateValueAndValidity();
    this.serviceSelected.emit(serviceId);
    setTimeout(() => {
      if (this.canProceedToNextStep()) {
        this.nextStep();
      }
    }, 100);
  }

  selectProfessional(professionalId: string | null): void {
    this.form.patchValue({ professionalId: professionalId || '' });
    this.professionalIdValue.set(professionalId || '');
    this.professionalSelected.emit(professionalId);
    setTimeout(() => {
      if (this.currentStep() === 2 && this.canProceedToNextStep()) {
        this.nextStep();
      }
    }, 150);
  }

  formatDuration(minutes: number | null | undefined): string {
    if (!minutes) return '';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours} ${hours === 1 ? 'hora' : 'horas'}`;
    return `${hours} h ${mins} min`;
  }

  selectTime(timeValue: string): void {
    this.form.patchValue({ time: timeValue });
    this.timeValue.set(timeValue);
    this.form.get('time')?.markAsTouched();
    this.form.get('time')?.updateValueAndValidity();
  }

  onDateSelectedFromCalendar(dateStr: string): void {
    this.form.patchValue({ date: dateStr }, { emitEvent: false });
    this.dateValue.set(dateStr);

    // Clear selected time when date changes
    this.form.patchValue({ time: '' }, { emitEvent: false });
    this.timeValue.set('');

    // Emit to parent to reload time slots
    this.dateSelected.emit(dateStr);
  }

  clearSelectedDate(): void {
    this.form.patchValue({ date: '', time: '' }, { emitEvent: false });
    this.dateValue.set('');
    this.timeValue.set('');
  }

  onCalendarMonthChanged(event: { year: number; month: number }): void {
    this.monthChanged.emit(event);
  }

  nextStep(): void {
    if (!this.canProceedToNextStep()) return;
    const current = this.currentStep();
    if (current < this.totalSteps) {
      this.currentStep.set(current + 1);
      this.stepChange.emit(this.currentStep());
      this.scrollToTop();

      // Apply cached phone data when reaching step 4
      if (this.currentStep() === 4) {
        this.applyCachedPhone();
      }
    }
  }

  previousStep(): void {
    const current = this.currentStep();
    if (current > 1) {
      this.currentStep.set(current - 1);
      this.stepChange.emit(this.currentStep());
      this.scrollToTop();
    } else if (current === 1) {
      this.cancelWizard.emit();
    }
  }

  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps && step <= this.currentStep()) {
      this.currentStep.set(step);
      this.stepChange.emit(step);
      this.scrollToTop();
      if (step === 4) {
        this.applyCachedPhone();
      }
    }
  }

  // On phones a long list can leave the next step's title off-screen
  private scrollToTop(): void {
    if (typeof window !== 'undefined' && window.scrollY > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  submitBooking(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.getRawValue();
    this.saveCachedClientData();

    const bookingData: BookingWizardData = {
      serviceId: formValue.serviceId,
      professionalId: formValue.professionalId || null,
      date: formValue.date,
      time: formValue.time,
      clientName: formValue.clientName,
      phone: formValue.phone,
    };

    this.bookingComplete.emit(bookingData);
  }
}
